import { useContext, useEffect, useRef } from "react"

import { useTrans } from "@/i18n"

import { CardBody, CardWrapper } from "@/components/Card"
import { Heading } from "@/components/Typography"
import { Button } from "@/components/Button"
import { RecipientStatusBanner } from "@/components/forms/RecipientStatusBanner"

import { MangopayNotReadyOverlay } from "./_components/MangopayGate"
import { ScaAboutDisclosure } from "./_components/ScaAboutDisclosure"

import {
	FiCheckCircle,
	FiX,
	FiChevronRight,
	FiAlertTriangle,
} from "@/lib/icons"

import {
	MangopayScaConsentState,
	useUserContextQuery,
	useScaConsentManageMutation,
	useScaEnrollmentStartMutation,
	type ScaConsentManageMutation,
	type ScaEnrollmentStartMutation,
} from "@/api/graphql"
import { useApiV1InvestmentSessionRetrieve } from "@/api/rest/generated/api/api"

import { RedirectContext } from "@/context/redirectContext"
import { useToasts } from "@/context/useToasts"
import {
	ControlStatusEnum,
	useHiddenGetParamsContext,
} from "@/context/useHiddenGetParams"

import { captureMessage } from "@/lib/sentry"
import { cn } from "@/lib/utils"

type ConsentKey =
	| "sca_consent_contact_information"
	| "sca_consent_view_account_information"
	| "sca_consent_recipient_registration"
	| "sca_consent_transfer"

const CONSENT_ROWS: {
	key: ConsentKey
	titleKey: string
	descriptionKey: string
}[] = [
	{
		key: "sca_consent_transfer",
		titleKey: "investor:mangopay.consent.transfer.title",
		descriptionKey: "investor:mangopay.consent.transfer.description",
	},
	{
		key: "sca_consent_recipient_registration",
		titleKey: "investor:mangopay.consent.recipient_registration.title",
		descriptionKey:
			"investor:mangopay.consent.recipient_registration.description",
	},
	{
		key: "sca_consent_view_account_information",
		titleKey: "investor:mangopay.consent.view_account_information.title",
		descriptionKey:
			"investor:mangopay.consent.view_account_information.description",
	},
	{
		key: "sca_consent_contact_information",
		titleKey: "investor:mangopay.consent.contact_information.title",
		descriptionKey:
			"investor:mangopay.consent.contact_information.description",
	},
]

export function InvestorPayments() {
	const t = useTrans("investor")

	const { data, isLoading } = useUserContextQuery()
	const { data: sessionData } = useApiV1InvestmentSessionRetrieve()

	const { redirect } = useContext(RedirectContext)
	const { addToast } = useToasts()
	const { controlStatus } = useHiddenGetParamsContext()

	const { mutate: startScaEnrollment, isPending: isEnrolling } =
		useScaEnrollmentStartMutation()
	const { mutate: manageScaConsent, isPending: isManaging } =
		useScaConsentManageMutation()

	// Round-trip handoff after Mangopay SCA.
	//
	// Flow: user lands here → clicks Grant / Manage → `redirect()` sets
	// `window.location.href` (full page navigation, no SPA state kept) → the
	// Mangopay hosted page runs → Mangopay sends the browser back with a
	// `?controlStatus=VALIDATED|FAILED` query param, which `HiddenGetParams`
	// strips off the URL and exposes via `controlStatus` → React boots fresh,
	// React Query cache is empty, so `useUserContextQuery` and
	// `useApiV1InvestmentSessionRetrieve` both refetch → if every consent is
	// now `Active` and the user still has a pending investment, this effect
	// fires and sends them back to that investment URL so they can finish
	// paying. If any consent is still missing, the effect doesn't fire and
	// the user sees the "incomplete" alert on this page.
	//
	// Gating on `controlStatus` is what scopes the redirect to the actual
	// Mangopay round-trip — without it, a fully-enrolled investor with a
	// pending investment who clicks the Payments tab would be bounced back
	// to the investment URL on every visit, with no way to reach the consent
	// list to review or revoke a permission.
	//
	// Hoisted above `isLoading` so the hook call order stays stable across
	// renders (the early returns below would otherwise break Rules of Hooks).
	const scaData = data?.me?.mangopay_user?.sca
	const visibleRows = CONSENT_ROWS.filter(
		(row) =>
			scaData &&
			scaData[row.key] !== MangopayScaConsentState.NotApplicable,
	)
	const allConsentsGranted = Boolean(
		scaData?.sca_enrolled &&
			visibleRows.every(
				(row) => scaData[row.key] === MangopayScaConsentState.Active,
			),
	)
	const pendingInvestmentUrl = sessionData?.pending_investment?.url
	const didResumeInvestmentRef = useRef(false)
	useEffect(() => {
		if (didResumeInvestmentRef.current) return
		if (controlStatus !== ControlStatusEnum.VALIDATED) return
		if (!allConsentsGranted || !pendingInvestmentUrl) return
		didResumeInvestmentRef.current = true
		redirect(pendingInvestmentUrl)
	}, [controlStatus, allConsentsGranted, pendingInvestmentUrl, redirect])

	if (isLoading) {
		return null
	}

	const hasMangopayUser = data?.me?.has_mangopay_user === true

	// `has_mangopay_user === true` with `mangopay_user.sca === null` is
	// representable in the schema — treat it the same as "no Mangopay user"
	// so the user sees the support-contact card instead of a blank page.
	if (!hasMangopayUser || !scaData) {
		return (
			<>
				<title>
					{t("common:web.title", {
						page: t("investor:mangopay.title"),
					})}
				</title>
				<CardWrapper className="relative flex min-h-[60vh] flex-col">
					<CardBody className="grow">
						<Heading
							as="h2"
							styleAs="h5"
							className="mb-3 sm:truncate"
						>
							{t("investor:mangopay.title")}
						</Heading>
						<p className="text-sm text-gray-600">
							{t("investor:mangopay.copy")}
						</p>
					</CardBody>
					<MangopayNotReadyOverlay />
				</CardWrapper>
			</>
		)
	}

	const showWalletUnavailableToast = () => {
		addToast({
			variant: "alert",
			id: "investor-payments-wallet-unavailable",
			text: t("investor:identity.copy.mangopay.error.wallet_unavailable"),
		})
	}

	const handleEnrollSuccess = (result: ScaEnrollmentStartMutation) => {
		const redirectUrl = result?.sca_enrollment_start?.redirect_url
		if (redirectUrl) {
			redirect(redirectUrl)
			return
		}
		captureMessage("User unable to enroll SCA from investor payments", {
			level: "error",
			extra: { reason: "missing_redirect_url", result },
		})
		showWalletUnavailableToast()
	}

	const handleEnrollError = (error: unknown) => {
		captureMessage("User unable to enroll SCA from investor payments", {
			level: "error",
			extra: { reason: "mutation_error", error },
		})
		showWalletUnavailableToast()
	}

	const handleManageSuccess = (result: ScaConsentManageMutation) => {
		// Mangopay requires SCA enrollment first; chain into the enrollment flow.
		if (result?.sca_consent_manage?.sca_required) {
			// return_url is capped at 50 chars server-side and would blow the
			// cap on preview hosts (~71 chars) or on any prod URL that carries
			// a query string. Omit it and let the backend redirect to its
			// default page after Mangopay's session.
			startScaEnrollment(
				{},
				{
					onSuccess: handleEnrollSuccess,
					onError: handleEnrollError,
				},
			)
			return
		}

		const redirectUrl = result?.sca_consent_manage?.redirect_url
		if (redirectUrl) {
			redirect(redirectUrl)
			return
		}

		captureMessage(
			"User unable to manage SCA consent from investor payments",
			{
				level: "error",
				extra: { reason: "missing_redirect_url", result },
			},
		)
		showWalletUnavailableToast()
	}

	const handleManageError = (error: unknown) => {
		captureMessage(
			"User unable to manage SCA consent from investor payments",
			{
				level: "error",
				extra: { reason: "mutation_error", error },
			},
		)
		showWalletUnavailableToast()
	}

	const handleManageClick = () => {
		manageScaConsent(
			{ return_url: window.location.href },
			{
				onSuccess: handleManageSuccess,
				onError: handleManageError,
			},
		)
	}

	const handleEnrollClick = () => {
		// return_url is capped at 50 chars server-side and would blow the cap
		// on preview hosts (~71 chars) or on any prod URL that carries a query
		// string. Omit it and let the backend redirect to its default page
		// after Mangopay's session.
		startScaEnrollment(
			{},
			{
				onSuccess: handleEnrollSuccess,
				onError: handleEnrollError,
			},
		)
	}

	return (
		<>
			<title>
				{t("common:web.title", {
					page: t("investor:mangopay.title"),
				})}
			</title>
			<CardWrapper>
				<CardBody>
					<Heading as="h2" styleAs="h5" className="sm:truncate">
						{t("investor:mangopay.title")}
					</Heading>

					<img
						src="/assets/partners/mangopay_logo.png"
						alt="Mangopay logo"
						className="my-6 w-28"
					/>

					<p className="mb-4 text-sm whitespace-break-spaces text-gray-600">
						{t("investor:mangopay.copy")}
					</p>

					{scaData?.sca_enrolled && (
						<ConsentStatusAlert
							allGranted={allConsentsGranted}
							t={t}
						/>
					)}

					<div className="space-y-4">
						{/* Enrollment status */}
						<div className="flex items-start justify-between gap-4 rounded-md border border-gray-200 p-4">
							<div>
								<h3 className="text-sm font-medium text-gray-900">
									{t("investor:mangopay.enrollment.title")}
								</h3>
								<p className="mt-1 text-sm text-gray-600">
									{t(
										"investor:mangopay.enrollment.description",
									)}
								</p>
							</div>
							<StatusPill
								enabled={Boolean(scaData?.sca_enrolled)}
								t={t}
							/>
						</div>

						{/* Recipient status — reuse existing banner */}
						{scaData?.recipient_status && (
							<RecipientStatusBanner
								status={scaData?.recipient_status}
								maskedIban={
									data?.me?.investor?.masked_iban ?? null
								}
							/>
						)}

						{/* Consents. Only meaningful post-enrollment — before enrollment
						    every row is inactive and the user has no way to grant
						    consent yet. Mangopay controls which permissions its hosted
						    page renders; we can't scope the request per the schema
						    docs on sca_consent_manage. */}
						{scaData?.sca_enrolled && visibleRows.length > 0 && (
							<div className="rounded-md border border-gray-200">
								<div className="border-b border-gray-200 p-4">
									<h3 className="text-sm font-medium text-gray-900">
										{t("investor:mangopay.consent.title")}
									</h3>
									<p className="mt-1 text-sm text-gray-600">
										{t(
											"investor:mangopay.consent.description",
										)}
									</p>
								</div>
								<ul className="divide-y divide-gray-200">
									{visibleRows.map((row) => (
										<li
											key={row.key}
											className="flex items-start justify-between gap-4 p-4"
										>
											<div className="min-w-0">
												<p className="text-sm font-medium text-gray-900">
													{t(row.titleKey)}
												</p>
												<p className="mt-1 text-sm text-gray-600">
													{t(row.descriptionKey)}
												</p>
											</div>
											<ConsentStatusPill
												state={scaData[row.key]}
												t={t}
											/>
										</li>
									))}
								</ul>
							</div>
						)}

						<ScaAboutDisclosure />

						<div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
							<Button
								type="button"
								onClick={
									scaData?.sca_enrolled
										? handleManageClick
										: handleEnrollClick
								}
								disabled={isManaging || isEnrolling}
							>
								{t(
									!scaData?.sca_enrolled
										? "investor:mangopay.enrollment.action"
										: allConsentsGranted
											? "investor:mangopay.consent.action"
											: "investor:mangopay.consent.action_grant",
								)}
								<FiChevronRight className="ml-2" />
							</Button>
						</div>
					</div>
				</CardBody>
			</CardWrapper>
		</>
	)
}

function ConsentStatusAlert({
	allGranted,
	t,
}: {
	allGranted: boolean
	t: (key: string) => string
}) {
	const config = allGranted
		? {
				container: "bg-green-50",
				titleClass: "text-green-800",
				bodyClass: "text-green-700",
				icon: <FiCheckCircle className="size-5 text-green-500" />,
				titleKey: "investor:mangopay.consent.alert.complete.title",
				bodyKey: "investor:mangopay.consent.alert.complete.body",
			}
		: {
				container: "bg-yellow-50",
				titleClass: "text-yellow-800",
				bodyClass: "text-yellow-700",
				icon: <FiAlertTriangle className="size-5 text-yellow-500" />,
				titleKey: "investor:mangopay.consent.alert.incomplete.title",
				bodyKey: "investor:mangopay.consent.alert.incomplete.body",
			}

	return (
		<div
			className={cn("mb-4 rounded-md p-4", config.container)}
			data-testid="mangopay-consent-alert"
			data-complete={allGranted}
		>
			<div className="flex items-start">
				<div className="shrink-0">{config.icon}</div>
				<div className="ml-3">
					<h3
						className={cn("text-sm font-medium", config.titleClass)}
					>
						{t(config.titleKey)}
					</h3>
					<p className={cn("mt-1 text-sm", config.bodyClass)}>
						{t(config.bodyKey)}
					</p>
				</div>
			</div>
		</div>
	)
}

function StatusPill({
	enabled,
	t,
}: {
	enabled: boolean
	t: (key: string) => string
}) {
	return (
		<span
			className={cn(
				"inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
				enabled
					? "bg-green-50 text-green-800"
					: "bg-gray-100 text-gray-600",
			)}
			data-testid="mangopay-status-pill"
			data-enabled={enabled}
		>
			{enabled ? (
				<FiCheckCircle className="size-3.5" />
			) : (
				<FiX className="size-3.5" />
			)}
			{t(
				enabled
					? "investor:mangopay.status.enabled"
					: "investor:mangopay.status.disabled",
			)}
		</span>
	)
}

function ConsentStatusPill({
	state,
	t,
}: {
	state: MangopayScaConsentState
	t: (key: string) => string
}) {
	// NOT_APPLICABLE rows are filtered out before render; treat as INACTIVE for
	// safety if a caller does render one.
	const enabled = state === MangopayScaConsentState.Active
	return <StatusPill enabled={enabled} t={t} />
}
